import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMonitor, FiBarChart3, FiTrendingUp, FiUsers, FiGlobe, FiMail, FiCalendar, FiAlertCircle, FiCheckCircle, FiClock, FiTarget, FiFilter } = FiIcons;

const DashboardTemplate = () => {
  const [activeView, setActiveView] = useState('overview');

  const kpiWidgets = [
    {
      title: 'Website Traffic',
      value: '12,543',
      change: '+15.2%',
      trend: 'up',
      icon: FiGlobe,
      color: 'bg-blue-500'
    },
    {
      title: 'Email Open Rate',
      value: '24.8%',
      change: '+3.1%',
      trend: 'up',
      icon: FiMail,
      color: 'bg-emerald-500'
    },
    {
      title: 'Conversion Rate',
      value: '3.2%',
      change: '-0.5%',
      trend: 'down',
      icon: FiTarget,
      color: 'bg-purple-500'
    },
    {
      title: 'Tasks Completed',
      value: '47/52',
      change: '+12',
      trend: 'up',
      icon: FiCheckCircle,
      color: 'bg-orange-500'
    }
  ];

  const recentTasks = [
    {
      task: 'Update homepage hero section',
      status: 'completed',
      priority: 'high',
      dueDate: 'Today',
      assignee: 'Marketing Team'
    },
    {
      task: 'Launch weekly newsletter campaign',
      status: 'in-progress',
      priority: 'high',
      dueDate: 'Tomorrow',
      assignee: 'Email Team'
    },
    {
      task: 'SEO audit for product pages',
      status: 'pending',
      priority: 'medium',
      dueDate: 'This week',
      assignee: 'SEO Specialist'
    },
    {
      task: 'Monthly analytics report',
      status: 'pending',
      priority: 'low',
      dueDate: 'Next week',
      assignee: 'Analytics Team'
    }
  ];

  const campaignPerformance = [
    {
      name: 'Spring Product Launch',
      type: 'Email Campaign',
      status: 'active',
      performance: 'excellent',
      metrics: { opens: '28.5%', clicks: '5.2%', conversions: '2.1%' }
    },
    {
      name: 'SEO Content Series',
      type: 'Content Marketing',
      status: 'active',
      performance: 'good',
      metrics: { traffic: '+45%', engagement: '4.2min', bounce: '32%' }
    },
    {
      name: 'Social Media Push',
      type: 'Social Campaign',
      status: 'completed',
      performance: 'good',
      metrics: { reach: '125K', engagement: '3.8%', shares: '1.2K' }
    }
  ];

  const dashboardViews = [
    { id: 'overview', label: 'Overview', icon: FiMonitor },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart3 },
    { id: 'campaigns', label: 'Campaigns', icon: FiTarget },
    { id: 'tasks', label: 'Tasks', icon: FiCheckCircle }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Dashboard Template - Command Center</h1>
        <p className="text-lg text-gray-600 max-w-3xl">
          Complete marketing coordinator dashboard with KPIs, task management, campaign tracking, 
          and real-time performance monitoring for efficient operations management.
        </p>
      </motion.div>

      {/* Navigation Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-lg"
      >
        {dashboardViews.map((view) => (
          <button
            key={view.id}
            onClick={() => setActiveView(view.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-all duration-200 ${
              activeView === view.id
                ? 'bg-white text-emerald-600 shadow-sm'
                : 'text-gray-600 hover:text-emerald-600'
            }`}
          >
            <SafeIcon icon={view.icon} className="w-4 h-4" />
            <span>{view.label}</span>
          </button>
        ))}
      </motion.div>

      {/* Overview Dashboard */}
      {activeView === 'overview' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* KPI Widgets */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Performance Indicators</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {kpiWidgets.map((kpi, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card rounded-xl p-6 hover-lift"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 ${kpi.color} rounded-lg flex items-center justify-center`}>
                      <SafeIcon icon={kpi.icon} className="w-6 h-6 text-white" />
                    </div>
                    <div className={`flex items-center space-x-1 text-sm ${
                      kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <SafeIcon icon={kpi.trend === 'up' ? FiTrendingUp : FiTrendingUp} className="w-4 h-4" />
                      <span>{kpi.change}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">{kpi.value}</div>
                    <div className="text-sm text-gray-600">{kpi.title}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Recent Tasks & Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Tasks</h2>
              <div className="glass-card rounded-xl p-6">
                <div className="space-y-4">
                  {recentTasks.map((task, index) => (
                    <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-3 h-3 ${getPriorityColor(task.priority)} rounded-full`}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{task.task}</p>
                        <p className="text-xs text-gray-500">{task.assignee} • {task.dueDate}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                        {task.status.replace('-', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
                <button className="w-full mt-4 py-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                  View All Tasks
                </button>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">System Alerts</h2>
              <div className="glass-card rounded-xl p-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg">
                    <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email deliverability warning</p>
                      <p className="text-xs text-gray-600">Bounce rate increased to 3.2% - review list quality</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">SEO improvements detected</p>
                      <p className="text-xs text-gray-600">Organic traffic up 15% this week</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Scheduled maintenance</p>
                      <p className="text-xs text-gray-600">Website backup scheduled for tonight at 2 AM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Campaign Performance View */}
      {activeView === 'campaigns' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Campaign Performance</h2>
            <button className="flex items-center space-x-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors">
              <SafeIcon icon={FiFilter} className="w-4 h-4" />
              <span>Filter</span>
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {campaignPerformance.map((campaign, index) => (
              <div key={index} className="glass-card rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                    <p className="text-sm text-gray-600">{campaign.type}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      campaign.performance === 'excellent' ? 'bg-green-100 text-green-700' :
                      campaign.performance === 'good' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {campaign.performance}
                    </span>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                      {campaign.status}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-6">
                  {Object.entries(campaign.metrics).map(([key, value]) => (
                    <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-lg font-bold text-gray-900">{value}</div>
                      <div className="text-xs text-gray-600 capitalize">{key}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Dashboard Features */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-12 glass-card rounded-xl p-8"
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Dashboard Features & Capabilities</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiBarChart3} className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Real-time Analytics</h3>
            <p className="text-gray-600 text-sm">Live data updates with customizable refresh intervals</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiFilter} className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Advanced Filtering</h3>
            <p className="text-gray-600 text-sm">Multi-dimensional data filtering and segmentation</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUsers} className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Team Collaboration</h3>
            <p className="text-gray-600 text-sm">Shared views and collaborative task management</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardTemplate;